/* eslint-disable no-plusplus */

/* iceberg-ui */
import { Card, Flex } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Alert,
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

/*  Shared  */
import { Permission } from 'application/Shared/Components/Core';
import {
    AppPicker,
    BreachPicker,
    BusinessOwnerPicker,
    GroupPicker,
    LifecyclePicker,
    MainCategory,
    ManagedPicker,
    QuarterPicker,
    TagsPicker,
    TenantPicker,
    UserPicker,
    WeekPicker
} from 'application/Shared/Components/Pickers';
import PlatformPicker from 'application/Shared/Components/Pickers/Platform';
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';

/* NPM */
import {
    endOfDay,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek
} from 'date-fns';
import dayjs from 'dayjs';

/*  React  */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const getStartOfDay = (eventDateObject) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(startOfDay(date)).getTime();
};

const getEndOfDay = (eventDateObject) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(endOfDay(date)).getTime();
};

const getStartOfMonth = (eventDateObject) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(startOfMonth(date)).getTime();
};

const getEndOfMonth = (eventDateObject) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    const lastDay = new Date(endOfMonth(date));
    const today = new Date();
    return lastDay.getTime() > today.getTime()
        ? today.getTime()
        : lastDay.getTime();
};

const getStartOfQuarter = (eventDateObject) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(startOfQuarter(date)).getTime();
};

const getEndOfQuarter = (eventDateObject) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(endOfQuarter(date)).getTime();
};

const getStartOfWeek = (eventDateObject) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(startOfWeek(date)).getTime();
};

const getEndOfWeek = (eventDateObject) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    const lastDay = new Date(endOfWeek(date));
    const today = new Date();
    return lastDay.getTime() > today.getTime()
        ? today.getTime()
        : lastDay.getTime();
};

const clearReportSelections = ({
    reportConfiguration,
    setReportConfiguration
}) => {
    setReportConfiguration({
        ...reportConfiguration,
        reportSelections: {}
    });
};

const handleReportConfigurationChange = ({
    key,
    object,
    onChange,
    reportConfiguration,
    setReportConfiguration,
    subOptions,
    value = null
}) => {
    const { reportSelections } = reportConfiguration;
    let changeKey;
    let changeVal;
    const stateChanges = {};

    switch (key) {
        case 'startPeriod':
            changeKey = 'startTime';
            changeVal = new Date(dayjs(object).startOf('day')).getTime();
            break;
        case 'endPeriod':
            changeKey = 'endTime';
            changeVal = new Date(dayjs(object).endOf('day')).getTime();
            break;
        case 'startDay':
            changeKey = 'startTime';
            changeVal = getStartOfDay(object);
            break;
        case 'startMonth':
            changeKey = 'startTime';
            changeVal = getStartOfMonth(object);
            break;
        case 'startQuarter':
        case 'startWeek':
            changeKey = 'startTime';
            changeVal = new Date(object).getTime();
            break;
        case 'type':
            changeKey = key;
            changeVal = object.target.value;
            if (changeVal === 'application') {
                stateChanges.applicationOrCategorySelection = 'application';
            }
            if (changeVal === 'platform') {
                stateChanges.applicationOrCategorySelection = 'platform';
            }
            if (changeVal === 'offboard') {
                stateChanges.period = 'daily';
            }
            break;
        case 'userSelection':
            changeKey = key;
            if (typeof value === 'string') {
                changeVal = [value];
            } else {
                changeVal = value;
            }
            break;
        case 'applicationOrCategorySelection':
        case 'applicationSelection':
        case 'breachSelection':
        case 'categorySelection':
        case 'clientSelection':
        case 'discoveryOverviewMatrixTags':
        case 'groupSelection':
        case 'managementStageSelection':
        case 'period':
        case 'platformSelection':
        case 'statusSelection':
        case 'tagExclusion':
        case 'tagSelection':
        case 'userOrGroupSelection':
            changeKey = key;
            changeVal = value;
            break;
        case 'businessOwnerSelection':
            if (value !== 'notAssigned') {
                changeKey = key;
                changeVal = [value];
            } else {
                changeKey = key;
                changeVal = null;
            }
            break;
        default:
            if (typeof object.target.checked !== 'undefined') {
                changeKey = 'reportSelections';
                changeVal = {
                    ...reportSelections,
                    [key]: object.target.checked
                };
                if (typeof subOptions === 'object') {
                    subOptions.forEach((subOption) => {
                        // Don't select the hidden subOptions
                        if (!subOption.hidden) {
                            changeVal = {
                                ...changeVal,
                                [key + subOption.key]: object.target.checked
                            };
                        }
                    });
                }
            }
            break;
    }

    const stateChange = {
        reportConfiguration: {
            ...reportConfiguration,
            ...stateChanges,
            [changeKey]: changeVal
        }
    };

    // If we are choosing a day/month/quarter/week, we need to change both the start and end times.
    // startTime is set as a changeVal in the switch statement,
    // and since the above stateChange changes one value at a time,
    // we are adding the second change here.
    if (key === 'startDay') {
        stateChange.reportConfiguration.endTime = getEndOfDay(object);
    }

    if (key === 'startMonth') {
        stateChange.reportConfiguration.endTime = getEndOfMonth(object);
    }

    if (key === 'startQuarter') {
        stateChange.reportConfiguration.endTime = getEndOfQuarter(object);
    }

    if (key === 'startWeek') {
        stateChange.reportConfiguration.endTime = getEndOfWeek(object);
    }

    // If we are changing the period, then we need to calculate and set the default start and end times, too.
    if (changeKey === 'period') {
        if (changeVal === 'daily') {
            stateChange.reportConfiguration.startTime = getStartOfDay();
            stateChange.reportConfiguration.endTime = getEndOfDay();
        }
        if (changeVal === 'monthly') {
            stateChange.reportConfiguration.startTime = getStartOfMonth();
            stateChange.reportConfiguration.endTime = getEndOfMonth();
        }
        if (changeVal === 'period') {
            stateChange.reportConfiguration.startTime = getStartOfWeek();
            stateChange.reportConfiguration.endTime = getEndOfWeek();
        }
        if (changeVal === 'quarterly') {
            stateChange.reportConfiguration.startTime = getStartOfQuarter();
            stateChange.reportConfiguration.endTime = getEndOfQuarter();
        }
        if (changeVal === 'weekly') {
            stateChange.reportConfiguration.startTime = getStartOfWeek();
            stateChange.reportConfiguration.endTime = getEndOfWeek();
        }
    }

    setReportConfiguration(stateChange.reportConfiguration);

    if (key === 'type') {
        clearReportSelections({
            reportConfiguration,
            setReportConfiguration
        });
    }

    if (onChange) {
        onChange(stateChange.reportConfiguration);
    }
};

const RenderDatePicker = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    const { period, startTime, endTime } = reportConfiguration;

    if (period === 'daily') {
        return (
            <Flex
                data-trackid="reportBuilderRenderDatePickerDaily"
                sx={{ alignItems: 'baseline', marginTop: '8px' }}
            >
                <FormLabel component="label" sx={{ marginRight: '4px' }}>
                    {t('Reports.ReportBuilder.select_day')}:
                </FormLabel>
                <DatePicker
                    key={`renderDatePicker_${period}_${startTime}`}
                    value={dayjs(startTime)}
                    onChange={(e) =>
                        handleReportConfigurationChange({
                            key: 'startDay',
                            object: e,
                            onChange,
                            reportConfiguration,
                            setReportConfiguration
                        })
                    }
                    disableFuture
                />
            </Flex>
        );
    }

    if (period === 'weekly') {
        return (
            <Flex
                data-trackid="reportBuilderRenderDatePickerWeekly"
                sx={{ alignItems: 'baseline', marginTop: '8px' }}
            >
                <FormLabel component="label" sx={{ marginRight: '4px' }}>
                    {t('Reports.ReportBuilder.select_week')}:
                </FormLabel>
                <WeekPicker
                    value={dayjs(startTime)}
                    onChange={(e) =>
                        handleReportConfigurationChange({
                            key: 'startWeek',
                            object: e,
                            onChange,
                            reportConfiguration,
                            setReportConfiguration
                        })
                    }
                    disableFuture
                />
            </Flex>
        );
    }

    if (period === 'quarterly') {
        return (
            <Flex
                data-trackid="reportBuilderRenderDatePickerQuarterly"
                sx={{ alignItems: 'baseline', marginTop: '8px' }}
            >
                <FormLabel component="label" sx={{ marginRight: '4px' }}>
                    {t('Reports.ReportBuilder.select_quarter')}:
                </FormLabel>
                <QuarterPicker
                    value={dayjs(startTime)}
                    onChange={(e) =>
                        handleReportConfigurationChange({
                            key: 'startQuarter',
                            object: e,
                            onChange,
                            reportConfiguration,
                            setReportConfiguration
                        })
                    }
                    disableFuture
                />
            </Flex>
        );
    }

    if (period === 'monthly') {
        return (
            <Flex
                data-trackid="reportBuilderRenderDatePickerMonthly"
                sx={{ alignItems: 'baseline', marginTop: '8px' }}
            >
                <FormLabel component="label" sx={{ marginRight: '4px' }}>
                    {t('Reports.ReportBuilder.select_month')}:
                </FormLabel>
                <DatePicker
                    key={`renderDatePicker_${period}_${startTime}`}
                    openTo="month"
                    views={['year', 'month']}
                    value={dayjs(startTime)}
                    disableFuture
                    onChange={(e) =>
                        handleReportConfigurationChange({
                            key: 'startMonth',
                            object: e,
                            onChange,
                            reportConfiguration,
                            setReportConfiguration
                        })
                    }
                />
            </Flex>
        );
    }

    if (period === 'period') {
        return (
            <Flex
                data-trackid="reportBuilderRenderDatePickerPeriod"
                sx={{
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    marginTop: '8px'
                }}
            >
                <Flex sx={{ alignItems: 'baseline', marginRight: '16px' }}>
                    <FormLabel component="label" sx={{ marginRight: '4px' }}>
                        {t('Reports.ReportBuilder.select_start')}:
                    </FormLabel>
                    <DatePicker
                        key={`renderDatePicker_${period}_${startTime}`}
                        value={dayjs(startTime)}
                        onChange={(e) =>
                            handleReportConfigurationChange({
                                key: 'startPeriod',
                                object: e,
                                onChange,
                                reportConfiguration,
                                setReportConfiguration
                            })
                        }
                        disableFuture
                    />
                </Flex>
                <Flex sx={{ alignItems: 'baseline' }}>
                    <FormLabel component="label" sx={{ marginRight: '4px' }}>
                        {t('Reports.ReportBuilder.select_end')}:
                    </FormLabel>
                    <DatePicker
                        key={`renderDatePicker_${period}_${endTime}`}
                        value={dayjs(endTime)}
                        onChange={(e) =>
                            handleReportConfigurationChange({
                                key: 'endPeriod',
                                object: e,
                                onChange,
                                reportConfiguration,
                                setReportConfiguration
                            })
                        }
                        disableFuture
                    />
                </Flex>
            </Flex>
        );
    }

    return null;
};

const load = ({
    allReportsDescriptions,
    ignoreParams,
    onChange,
    reportConfiguration,
    reportSetup,
    runFn,
    setLoaded,
    setReportConfiguration
}) => {
    const state = {};

    if (!ignoreParams) {
        const params = Helpers.parseQueryString(window.location.search);

        state.reportConfiguration = { ...reportConfiguration };

        if (params.startTime) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                startTime: parseInt(params.startTime, 10)
            };
        }
        if (params.endTime) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                endTime: parseInt(params.endTime, 10)
            };
        }
        if (params.period) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                period: params.period
            };
        }

        if (params.clientSelection) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                clientSelection: [params.clientSelection]
            };
        }
        if (params.userSelection) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                userOrGroupSelection: 'users',
                userSelection: [params.userSelection]
            };
        }
        if (params.application) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                applicationSelection: [params.application]
            };
        }
        if (params.platform) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                platformSelection: [params.platform]
            };
        }
        if (params.breachSelection) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                breachSelection: [params.breachSelection]
            };
        }
        if (params.reportType) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                type: params.reportType
            };
        }
        if (params.disableBrowsers) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                disableBrowsers: params.disableBrowsers
            };
        }
        if (params.disablePersonal) {
            state.reportConfiguration = {
                ...state.reportConfiguration,
                disablePersonal: params.disablePersonal
            };
        }

        // Initialize checkboxes to checked
        if (allReportsDescriptions) {
            // reportConfiguration is an object with information about all the (19) reports
            // we can generate at both the admin and partner level.
            // Each item in the object has some other properties that are important here for initializing the checkboxes to true:
            // `type`, a string, which is basically the name of the report, e.g., "discovery".
            // `checkboxes`, an array of objects where each key has properties:
            //     `name`, e.g., "Discovery"
            //     `reportEndpoint`, e.g., "discovery/"
            //     `type`, e.g., "discovery"
            //     `subOptions`, an array of object that describes the child checkboxes under the main checkboxes.
            //           e.g., "[{key: 'Overview', name: 'Overview'}", {key: 'RiskAnalysis', name: 'Risk Analysis'}, etc.]

            // Therefore, we first start isolating the only entry that matches the report we want to run
            const selectedReport = allReportsDescriptions.find(
                (singleReport) =>
                    singleReport.type === state.reportConfiguration.type
            );

            // If there is such report, then get the original reportSelections and overwrite it
            // with the "report selected" state i.e., "true", e.g., `{discovery: true}`
            if (selectedReport) {
                const parentType = selectedReport.type;
                state.reportConfiguration.reportSelections = {
                    ...state.reportConfiguration.reportSelections,
                    [parentType]: true
                };

                // Now, for the report above, there will be checkboxes, and for each of the checkboxes,
                // set the checkbox-selected state i.e., "true".
                // For that, we need to iterate over the checkboxes object.
                // This will change the reportSelections to something like
                // {discovery: true, discoveryDiscovery: true}
                selectedReport.checkboxes.forEach((checkboxItem) => {
                    state.reportConfiguration.reportSelections = {
                        ...state.reportConfiguration.reportSelections,
                        [parentType + checkboxItem.type]: true
                    };

                    // These checkboxes will have subOptions, e.g., "discoveryDiscovery" will have Overview, Risk Analysis, etc.
                    // We need to set them to true (i.e., checked checkbox)
                    // This will change the reportSelections to something like
                    // {discovery: true, discoveryDiscovery: true, discoveryDiscoveryRiskAnalysis: true, etc.}
                    // We use the default value for the subOption (if available), and if there is, we set it to true.
                    Object.values(checkboxItem.subOptions).forEach(
                        (subOption) => {
                            if (!subOption.hidden) {
                                state.reportConfiguration.reportSelections = {
                                    ...state.reportConfiguration
                                        .reportSelections,
                                    [parentType +
                                    checkboxItem.type +
                                    subOption.key]:
                                        'default' in subOption
                                            ? subOption.default
                                            : true
                                };
                            }
                        }
                    );
                });
            }
        }

        // Setting default period for reports
        switch (state.reportConfiguration.type) {
            case 'businessOwner':
            case 'contracts':
            case 'qbr':
                state.reportConfiguration.period = 'quarterly';
                state.reportConfiguration.startTime = getStartOfQuarter();
                state.reportConfiguration.endTime = getEndOfQuarter();
                break;
            case 'productAdoption':
            case 'organizational':
                state.reportConfiguration.period = 'monthly';
                state.reportConfiguration.startTime = getStartOfMonth();
                state.reportConfiguration.endTime = getEndOfMonth();
                break;
            case 'offboard':
                state.reportConfiguration.userOrGroupSelection = 'users';
                break;
            default:
                break;
        }

        if (onChange) {
            onChange(state.reportConfiguration);
        }

        setReportConfiguration(state.reportConfiguration);

        if (params.run === 'true' && runFn) {
            runFn(state.reportConfiguration);
        }
    } else if (Object.keys(reportSetup).length > 0) {
        state.reportConfiguration = reportSetup;
        setReportConfiguration(reportSetup);
    }
    setLoaded(true);
};

const SelectTitle = ({ children }) => (
    <>
        <Typography
            sx={{
                color: 'text.secondary',
                fontSize: FontSizes[2],
                fontWeight: 600,
                marginBottom: '16px',
                marginTop: '8px'
            }}
        >
            {children}
        </Typography>
        <Divider
            sx={{
                borderColor: 'border.primary',
                marginBottom: '16px'
            }}
        />
    </>
);

const SectionContainer = ({ children, title }) => (
    <Card
        sx={{
            marginBottom: '16px',
            padding: '16px'
        }}
    >
        {title && <SelectTitle>{title}</SelectTitle>}
        {children}
    </Card>
);

const RenderClientOptions = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    const { clientSelection, includeInactive } = reportConfiguration;

    return (
        <SectionContainer title={t('Report.select_clients')}>
            <Grid
                container
                spacing={1}
                data-trackid="reportBuilderRenderClientOptions"
            >
                <Grid item md={4} xs={12}>
                    <TenantPicker
                        multiple
                        allValue={t('Common.all_clients')}
                        allValueId="0"
                        value={clientSelection}
                        onChange={(e) =>
                            handleReportConfigurationChange({
                                key: 'clientSelection',
                                object: null,
                                onChange,
                                reportConfiguration,
                                setReportConfiguration,
                                value: e
                            })
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeInactive}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    if (onChange) {
                                        onChange({
                                            ...reportConfiguration,
                                            includeInactive: checked
                                        });
                                    }
                                    setReportConfiguration({
                                        ...reportConfiguration,
                                        includeInactive: checked
                                    });
                                }}
                            />
                        }
                        label={t('Common.include_expired_disabled_users')}
                    />
                </Grid>
            </Grid>
        </SectionContainer>
    );
};

const RenderUserOptions = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    const { type, userOrGroupSelection, userSelection, groupSelection } =
        reportConfiguration;

    return (
        <SectionContainer
            title={
                type === 'offboard'
                    ? t('Reports.ReportBuilder.select_user')
                    : t('Reports.ReportBuilder.narrow_users')
            }
        >
            <Box data-trackid="reportBuilderRenderUserOptions">
                <RadioGroup
                    row
                    aria-label="userOrGroupSelection"
                    name="userOrGroupSelection"
                    value={userOrGroupSelection}
                    onChange={(e, v) =>
                        handleReportConfigurationChange({
                            key: 'userOrGroupSelection',
                            object: e,
                            onChange,
                            reportConfiguration,
                            setReportConfiguration,
                            value: v
                        })
                    }
                >
                    {type !== 'offboard' && (
                        <>
                            <FormControlLabel
                                value="all"
                                control={<Radio color="primary" />}
                                label={t(
                                    'Reports.ReportBuilder.all_users_and_groups'
                                )}
                            />
                            <FormControlLabel
                                value="users"
                                control={<Radio color="primary" />}
                                label={t('Reports.ReportBuilder.certain_users')}
                            />
                            {type !== 'browser' && (
                                <FormControlLabel
                                    value="groups"
                                    control={<Radio color="primary" />}
                                    label={t(
                                        'Reports.ReportBuilder.certain_groups'
                                    )}
                                />
                            )}
                        </>
                    )}
                </RadioGroup>
            </Box>
            {userOrGroupSelection === 'users' && (
                <Grid
                    container
                    spacing={1}
                    mt="16px"
                    data-trackid="reportBuilderRenderUserOptionsUsers"
                >
                    <Grid item md={4} xs={12}>
                        <FormLabel component="label">
                            {type === 'offboard'
                                ? t('Common.user')
                                : t('Common.users')}
                            :
                        </FormLabel>
                        <UserPicker
                            multiple={type !== 'offboard'}
                            allValue={
                                type === 'offboard'
                                    ? t('Common.no_user_selected')
                                    : t('Common.all_users')
                            }
                            allValueId="0"
                            value={userSelection}
                            onChange={(e) =>
                                handleReportConfigurationChange({
                                    key: 'userSelection',
                                    object: null,
                                    onChange,
                                    reportConfiguration,
                                    setReportConfiguration,
                                    value: e
                                })
                            }
                        />
                    </Grid>
                </Grid>
            )}
            {userOrGroupSelection === 'groups' && (
                <Grid
                    container
                    spacing={1}
                    data-trackid="reportBuilderRenderUserOptionsGroups"
                    mt="16px"
                >
                    <Grid item md={4} xs={12}>
                        <FormLabel component="label">
                            {t('Common.group')}:
                        </FormLabel>
                        <GroupPicker
                            multiple
                            allValue={t('Common.all_groups')}
                            allValueId="0"
                            value={groupSelection}
                            onChange={(e) =>
                                handleReportConfigurationChange({
                                    key: 'groupSelection',
                                    object: null,
                                    onChange,
                                    reportConfiguration,
                                    setReportConfiguration,
                                    value: e
                                })
                            }
                        />
                    </Grid>
                </Grid>
            )}
        </SectionContainer>
    );
};

const RenderDateOptions = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    const { type, period } = reportConfiguration;

    return (
        <SectionContainer title={t('Reports.ReportBuilder.select_period')}>
            <Grid
                container
                spacing={1}
                data-trackid="reportBuilderRenderDateOptions"
            >
                <Grid item xs={12}>
                    <RadioGroup
                        row
                        aria-label="period"
                        name="period"
                        value={period}
                        onChange={(e, v) =>
                            handleReportConfigurationChange({
                                key: 'period',
                                object: e,
                                onChange,
                                reportConfiguration,
                                setReportConfiguration,
                                value: v
                            })
                        }
                    >
                        {type !== 'productAdoption' &&
                            type !== 'qbr' &&
                            type !== 'applicationOverlap' && (
                                <>
                                    <FormControlLabel
                                        value="daily"
                                        control={<Radio color="primary" />}
                                        label={t('Common.day')}
                                    />

                                    <FormControlLabel
                                        value="weekly"
                                        control={<Radio color="primary" />}
                                        label={t('Common.week')}
                                    />

                                    <FormControlLabel
                                        value="monthly"
                                        control={<Radio color="primary" />}
                                        label={t('Common.month')}
                                    />
                                </>
                            )}

                        {type === 'qbr' && (
                            <FormControlLabel
                                value="quarterly"
                                control={<Radio color="primary" />}
                                label={t('Common.quarter')}
                            />
                        )}

                        {(type === 'organizational' ||
                            type === 'discovery' ||
                            type === 'userApps' ||
                            type === 'accountsInventory' ||
                            type === 'qbr') && (
                            <FormControlLabel
                                value="period"
                                control={<Radio color="primary" />}
                                label={t('Common.period')}
                            />
                        )}

                        {type === 'productAdoption' && (
                            <>
                                <FormControlLabel
                                    value="monthly"
                                    control={<Radio color="primary" />}
                                    label={t('Common.month')}
                                />
                                <FormControlLabel
                                    value="period"
                                    control={<Radio color="primary" />}
                                    label={t('Common.period')}
                                />
                            </>
                        )}
                        {type === 'applicationOverlap' && (
                            <>
                                <FormControlLabel
                                    value="weekly"
                                    control={<Radio color="primary" />}
                                    label={t('Common.week')}
                                />

                                <FormControlLabel
                                    value="monthly"
                                    control={<Radio color="primary" />}
                                    label={t('Common.month')}
                                />
                                <FormControlLabel
                                    value="period"
                                    control={<Radio color="primary" />}
                                    label={t('Common.period')}
                                />
                            </>
                        )}
                    </RadioGroup>
                </Grid>
            </Grid>
            <RenderDatePicker
                onChange={onChange}
                reportConfiguration={reportConfiguration}
                setReportConfiguration={setReportConfiguration}
            />
        </SectionContainer>
    );
};

const SelectOwner = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();

    return (
        <SectionContainer>
            <Grid
                container
                spacing={1}
                mt="0"
                data-trackid="businessOwnerSelectOwner"
            >
                <Grid item xs={12}>
                    <SelectTitle>
                        {t('Reports.ReportBuilder.select_business_owner')}
                    </SelectTitle>
                </Grid>
                <Grid item md={4} xs={12}>
                    <BusinessOwnerPicker
                        multiple={false}
                        disableAllOption
                        unselectedOption={t(
                            'ManageApplicationAdmin.no_user_selected'
                        )}
                        unselectedValue="notAssigned"
                        value={reportConfiguration.businessOwnerSelection}
                        onChange={(e) => {
                            setReportConfiguration({
                                ...reportConfiguration,
                                period: 'quarterly'
                            });
                            handleReportConfigurationChange({
                                key: 'businessOwnerSelection',
                                object: null,
                                onChange,
                                reportConfiguration,
                                setReportConfiguration,
                                value: e
                            });
                        }}
                    />
                </Grid>
            </Grid>
        </SectionContainer>
    );
};

const SelectQuarter = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();

    return (
        <SectionContainer title={t('Reports.ReportBuilder.select_quarter')}>
            <Box data-trackid="selectQuarter">
                <QuarterPicker
                    value={dayjs(reportConfiguration.startTime)}
                    onChange={(e) =>
                        handleReportConfigurationChange({
                            key: 'startQuarter',
                            object: e,
                            onChange,
                            reportConfiguration,
                            setReportConfiguration
                        })
                    }
                    disableFuture
                />
            </Box>
        </SectionContainer>
    );
};

const RenderAppButtons = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    const { applicationOrCategorySelection, type } = reportConfiguration;

    return (
        <SectionContainer
            title={t('Reports.ReportBuilder.narrow_applications')}
        >
            <RadioGroup
                row
                aria-label="applicationOrCategorySelection"
                name="applicationOrCategorySelection"
                value={applicationOrCategorySelection}
                onChange={(e, v) =>
                    handleReportConfigurationChange({
                        key: 'applicationOrCategorySelection',
                        object: e,
                        onChange,
                        reportConfiguration,
                        setReportConfiguration,
                        value: v
                    })
                }
            >
                <FormControlLabel
                    value="all"
                    control={<Radio color="primary" />}
                    label={t('Common.all')}
                />
                {type !== 'applicationOverlap' && (
                    <FormControlLabel
                        value="application"
                        control={<Radio color="primary" />}
                        label={t('Reports.ReportBuilder.certain_applications')}
                    />
                )}
                {type !== 'applicationOverlap' && (
                    <FormControlLabel
                        value="platform"
                        control={<Radio color="primary" />}
                        label={t(
                            'Reports.ReportBuilder.applications_platforms'
                        )}
                    />
                )}
                <FormControlLabel
                    value="category"
                    control={<Radio color="primary" />}
                    label={t('Reports.ReportBuilder.certain_categories')}
                />
                {type !== 'applicationOverlap' && (
                    <FormControlLabel
                        value="managementStage"
                        control={<Radio color="primary" />}
                        label={t(
                            'Reports.ReportBuilder.certain_management_stage'
                        )}
                    />
                )}
                {type !== 'applicationOverlap' && (
                    <FormControlLabel
                        value="status"
                        control={<Radio color="primary" />}
                        label={t('Reports.ReportBuilder.certain_statuses')}
                    />
                )}
                {type !== 'applicationOverlap' && (
                    <FormControlLabel
                        value="tag"
                        control={<Radio color="primary" />}
                        label={t('Common.select_tags')}
                    />
                )}
            </RadioGroup>
            <Box mt="16px">
                <RenderAppSelect
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                    showHeader={false}
                />

                {applicationOrCategorySelection === 'platform' && (
                    <RenderPlatformSelect
                        onChange={onChange}
                        reportConfiguration={reportConfiguration}
                        setReportConfiguration={setReportConfiguration}
                    />
                )}

                {applicationOrCategorySelection === 'category' && (
                    <SelectCategories
                        onChange={onChange}
                        reportConfiguration={reportConfiguration}
                        setReportConfiguration={setReportConfiguration}
                    />
                )}

                {applicationOrCategorySelection === 'managementStage' && (
                    <SelectManagementStage
                        onChange={onChange}
                        reportConfiguration={reportConfiguration}
                        setReportConfiguration={setReportConfiguration}
                    />
                )}

                {applicationOrCategorySelection === 'status' && (
                    <SelectLifecycleStage
                        onChange={onChange}
                        reportConfiguration={reportConfiguration}
                        setReportConfiguration={setReportConfiguration}
                    />
                )}

                <RenderTagOptions
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            </Box>
        </SectionContainer>
    );
};

const RenderPlatformSelect = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    const { platformSelection } = reportConfiguration;

    return (
        <Box data-trackid="reportBuilderRenderPlatformSelect">
            <FormLabel>{t('Common.application_platforms')}:</FormLabel>
            <PlatformPicker
                allApplications
                multiple
                allValue="All"
                allValueId="0"
                value={platformSelection}
                onChange={(e) =>
                    handleReportConfigurationChange({
                        key: 'platformSelection',
                        object: null,
                        onChange,
                        reportConfiguration,
                        setReportConfiguration,
                        value: e
                    })
                }
            />
            <Box mt="16px">
                <RenderAppSelect
                    forceRender
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            </Box>
        </Box>
    );
};

/**
 *
 * @param {*} forceRender - Show the Application Selection dropdown when set to true.
 *                          When set to false, the application selection dropdown will be shown based off of type and category selections
 * @returns
 */
const RenderAppSelect = ({
    forceRender = false,
    onChange,
    reportConfiguration,
    setReportConfiguration,
    showHeader
}) => {
    const { t } = useTranslation();
    const { type, applicationOrCategorySelection, applicationSelection } =
        reportConfiguration;

    if (
        applicationOrCategorySelection !== 'application' &&
        type !== 'application' &&
        type !== 'productAdoption' &&
        !forceRender
    ) {
        return null;
    }

    return (
        <Box data-trackid="reportBuilderRenderAppSelect">
            {showHeader && (
                <SelectTitle>
                    {t('Reports.ReportBuilder.narrow_applications')}
                </SelectTitle>
            )}
            <FormLabel>{t('Common.select_application(s)')}:</FormLabel>
            <AppPicker
                allApplications
                multiple={type !== 'application'}
                max={1}
                allValue={
                    type !== 'application' && type !== 'productAdoption'
                        ? t('Common.all_applications')
                        : t('Common.unselected')
                }
                allValueId="0"
                value={applicationSelection}
                onChange={(e) =>
                    handleReportConfigurationChange({
                        key: 'applicationSelection',
                        object: null,
                        onChange,
                        reportConfiguration,
                        setReportConfiguration,
                        value: e
                    })
                }
            />
            {type === 'productAdoption' && applicationSelection.length > 4 && (
                <Alert severity="error" icon={false}>
                    {t('Reports.ReportBuilder.tooManyAppsWarning')}
                </Alert>
            )}
        </Box>
    );
};

const SelectCategories = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    return (
        <Grid container spacing={1} mt="0">
            <Grid item xs={12}>
                <FormLabel component="label">
                    {t('Common.select_categories')}:
                </FormLabel>
                <MainCategory
                    multiple
                    allValue={t('Common.all_categories')}
                    allValueId="0"
                    value={reportConfiguration.categorySelection}
                    onChange={(e) =>
                        handleReportConfigurationChange({
                            key: 'categorySelection',
                            object: null,
                            onChange,
                            reportConfiguration,
                            setReportConfiguration,
                            value: e
                        })
                    }
                />
            </Grid>
        </Grid>
    );
};

const SelectManagementStage = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();

    return (
        <Grid container spacing={1} mt="0">
            <Grid item xs={12}>
                <FormLabel component="label">
                    {t('Common.select_management_stage')}:
                </FormLabel>
                <ManagedPicker
                    multiple
                    allValue="All"
                    allValueId="0"
                    value={reportConfiguration.managementStageSelection}
                    onChange={(e) =>
                        handleReportConfigurationChange({
                            key: 'managementStageSelection',
                            object: null,
                            onChange,
                            reportConfiguration,
                            setReportConfiguration,
                            value: e
                        })
                    }
                />
            </Grid>
        </Grid>
    );
};

const SelectLifecycleStage = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    return (
        <Grid container spacing={1} mt="0">
            <Grid item xs={12}>
                <FormLabel component="label">
                    {t('Common.select_statuses')}:
                </FormLabel>
                <LifecyclePicker
                    multiple
                    allValue="All"
                    allValueId="0"
                    value={reportConfiguration.statusSelection}
                    onChange={(e) =>
                        handleReportConfigurationChange({
                            key: 'statusSelection',
                            object: null,
                            onChange,
                            reportConfiguration,
                            setReportConfiguration,
                            value: e
                        })
                    }
                />
            </Grid>
        </Grid>
    );
};

const RenderTagSelect = ({
    onChange,
    reportConfiguration,
    setReportConfiguration,
    tagKey
}) => {
    const { t } = useTranslation();
    const { tagSelection, tagExclusion, discoveryOverviewMatrixTags } =
        reportConfiguration;

    let disableAllOption = false;
    let unselectedOption = false;
    let unselectedValue = false;
    let label = t('Common.select_tags');
    let allValue = 'All';
    let value = tagSelection;

    switch (tagKey) {
        case 'discoveryOverviewMatrixTags':
            disableAllOption = true;
            unselectedOption = t('Tags.unselected');
            unselectedValue = ['0'];
            value = discoveryOverviewMatrixTags;
            break;
        case 'tagExclusion':
            label = t('ReportBuilder.exclude_tags');
            value = tagExclusion;
            allValue = 'N/A';
            break;
        default:
            break;
    }

    return (
        <Box data-trackid="reportBuilderRenderTagSelect">
            <FormLabel>{label}:</FormLabel>
            <TagsPicker
                multiple
                allValue={allValue}
                allValueId="0"
                value={value}
                includeGlobalTags={false}
                includeTagSettings={false}
                disableAllOption={disableAllOption}
                unselectedOption={unselectedOption}
                unselectedValue={unselectedValue}
                onChange={(e) =>
                    handleReportConfigurationChange({
                        key: tagKey,
                        object: null,
                        onChange,
                        reportConfiguration,
                        setReportConfiguration,
                        value: e
                    })
                }
            />
        </Box>
    );
};

const RenderTagOptions = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    const { applicationOrCategorySelection, includeUntagged } =
        reportConfiguration;

    if (applicationOrCategorySelection !== 'tag') {
        return null;
    }

    return (
        <Box data-trackid="reportBuilderRenderTagOptions">
            <Box mb="8px">
                <RenderTagSelect
                    tagKey="tagSelection"
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            </Box>
            <Box mb="8px">
                <RenderTagSelect
                    tagKey="tagExclusion"
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            </Box>
            <Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeUntagged}
                            onChange={(e) => {
                                const { checked } = e.target;
                                onChange({
                                    ...reportConfiguration,
                                    includeUntagged: checked
                                });
                                setReportConfiguration({
                                    ...reportConfiguration,
                                    includeUntagged: checked
                                });
                            }}
                        />
                    }
                    label={t('ReportBuilder.no_tags')}
                />
            </Box>
        </Box>
    );
};

const MainTagSelect = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();

    return (
        <SectionContainer
            title={t('Reports.ReportBuilder.overview_matrix_tags')}
        >
            <RenderTagSelect
                tagKey="discoveryOverviewMatrixTags"
                onChange={onChange}
                reportConfiguration={reportConfiguration}
                setReportConfiguration={setReportConfiguration}
            />
        </SectionContainer>
    );
};

const PartnerBreachImpact = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();

    return (
        <SectionContainer
            title={t('Reports.ReportBuilder.overview_matrix_tags')}
        >
            <FormLabel component="label">
                {t('VendorIncident.select')}:
            </FormLabel>
            <BreachPicker
                multiple
                disableAllOption
                unselectedOption={t('VendorIncident.last_90_days')}
                unselectedValue={['0']}
                value={reportConfiguration.breachSelection}
                onChange={(e) =>
                    handleReportConfigurationChange({
                        key: 'breachSelection',
                        object: null,
                        onChange,
                        reportConfiguration,
                        setReportConfiguration,
                        value: e
                    })
                }
            />
        </SectionContainer>
    );
};

const RenderSubReportOptions = ({
    checkbox,
    onChange,
    options,
    reportConfiguration,
    row,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    const { reportSelections } = reportConfiguration;

    return (
        <Box
            style={{ paddingLeft: '20px' }}
            data-trackid={`renderSubReportOptions_${row.type}_${checkbox.type}`}
        >
            {options.map((o) => {
                const { key, name, tooltip } = o;

                return (
                    <FormControlLabel
                        key={`checkbox_${row.type}${checkbox.type}${key}`}
                        disabled={!reportSelections[row.type + checkbox.type]}
                        control={
                            <Checkbox
                                checked={
                                    reportSelections[
                                        row.type + checkbox.type + key
                                    ] || false
                                }
                                onChange={(e) =>
                                    handleReportConfigurationChange({
                                        key: row.type + checkbox.type + key,
                                        object: e,
                                        onChange,
                                        reportConfiguration,
                                        setReportConfiguration
                                    })
                                }
                                name={key}
                                color="primary"
                            />
                        }
                        label={
                            <Box display="inline-flex" alignItems="center">
                                {name}
                                {tooltip && <TooltipQ text={t(tooltip)} />}
                            </Box>
                        }
                        data-trackid={`renderSubReportOptions_${row.type}_${checkbox.type}_${key}`}
                    />
                );
            })}
        </Box>
    );
};

const RenderSubReportCheckboxes = ({
    row,
    checkbox,
    numberOfColumns,
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();

    return (
        <Grid
            item
            xs={12}
            sm={numberOfColumns !== 0 ? 12 / numberOfColumns : 12}
            data-trackid={`renderSubReportCheckboxes_${row.type}_${checkbox.type}`}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={
                            reportConfiguration.reportSelections[
                                row.type + checkbox.type
                            ] || false
                        }
                        onChange={(e) =>
                            handleReportConfigurationChange({
                                key: row.type + checkbox.type,
                                object: e,
                                onChange,
                                reportConfiguration,
                                setReportConfiguration,
                                subOptions: checkbox.subOptions.filter((r) => {
                                    if (r.requirements) {
                                        return r.requirements.filter(
                                            (requirement) =>
                                                requirement.values?.includes(
                                                    reportConfiguration[
                                                        requirement.key
                                                    ]
                                                )
                                        ).length;
                                    }
                                    return true;
                                }),
                                value: null
                            })
                        }
                        name={checkbox.key}
                        color="primary"
                    />
                }
                label={t(`Reports.ProceduralCodes.${checkbox.name}`)}
            />
            <div style={{ paddingLeft: 30 }}>
                {checkbox.subOptions
                    .filter((r) => {
                        // Do not include Hidden Sub-Options
                        if (r.hidden) {
                            return false;
                        }

                        /* iterate through the requirements &
                        make sure that we meet one of them
                        before displaying the option. */
                        if (r.requirements) {
                            return r.requirements.filter((requirement) =>
                                requirement.values?.includes(
                                    reportConfiguration[requirement.key]
                                )
                            ).length;
                        }
                        return true;
                    })
                    .map((subOption) => (
                        <div
                            key={`checkbox_${row.type}${checkbox.type}${subOption.key}`}
                            data-trackid={`renderSubReportCheckboxes_subOption_${row.type}_${checkbox.type}_${subOption.key}`}
                        >
                            <FormControlLabel
                                disabled={
                                    !reportConfiguration.reportSelections[
                                        row.type + checkbox.type
                                    ]
                                }
                                control={
                                    <Checkbox
                                        checked={
                                            reportConfiguration
                                                .reportSelections[
                                                row.type +
                                                    checkbox.type +
                                                    subOption.key
                                            ] || false
                                        }
                                        onChange={(e) =>
                                            handleReportConfigurationChange({
                                                key:
                                                    row.type +
                                                    checkbox.type +
                                                    subOption.key,
                                                object: e,
                                                onChange,
                                                reportConfiguration,
                                                setReportConfiguration
                                            })
                                        }
                                        name={checkbox.key}
                                        color="primary"
                                    />
                                }
                                label={t(
                                    `Reports.ProceduralCodes.${subOption.name}`
                                )}
                            />
                            {subOption.options && (
                                <RenderSubReportOptions
                                    checkbox={checkbox}
                                    onChange={onChange}
                                    options={subOption.options}
                                    reportConfiguration={reportConfiguration}
                                    row={row}
                                    setReportConfiguration={
                                        setReportConfiguration
                                    }
                                />
                            )}
                        </div>
                    ))}
            </div>
        </Grid>
    );
};

const RenderSubReports = ({
    onChange,
    allReportsDescriptions,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();

    return (
        <SectionContainer title={t('Reports.ReportBuilder.select_sub_report')}>
            {allReportsDescriptions.map((row) => (
                <Box key={`subopts_${row.type}`}>
                    {row.type === reportConfiguration.type && (
                        <Grid container spacing={1} mt="0">
                            {row.checkboxes
                                .filter((r) => {
                                    /* iterate through the requirements &
                                    make sure that we meet one of them
                                    before displaying the option */
                                    if (r.requirements) {
                                        return r.requirements.filter(
                                            (requirement) =>
                                                requirement.values?.includes(
                                                    reportConfiguration[
                                                        requirement.key
                                                    ]
                                                )
                                        ).length;
                                    }
                                    return true;
                                })
                                .map((checkbox) => (
                                    <RenderSubReportCheckboxes
                                        key={`checkbox_${row.type}${checkbox.type}`}
                                        row={row}
                                        checkbox={checkbox}
                                        numberOfColumns={row.checkboxes.length}
                                        onChange={onChange}
                                        reportConfiguration={
                                            reportConfiguration
                                        }
                                        setReportConfiguration={
                                            setReportConfiguration
                                        }
                                    />
                                ))}
                        </Grid>
                    )}
                </Box>
            ))}
        </SectionContainer>
    );
};

const RenderAdvanced = ({
    onChange,
    reportConfiguration,
    setReportConfiguration
}) => {
    const { t } = useTranslation();
    const { type } = reportConfiguration;

    return (
        <SectionContainer title={t('Reports.advanced_setup')}>
            <Flex sx={{ flexDirection: 'column', marginTop: '16px' }}>
                {type !== 'applicationOverlap' && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={reportConfiguration.disableBrowsers}
                                color="primary"
                                onChange={(e) => {
                                    onChange({
                                        ...reportConfiguration,
                                        disableBrowsers: e.target.checked
                                    });
                                    setReportConfiguration({
                                        ...reportConfiguration,
                                        disableBrowsers: e.target.checked
                                    });
                                }}
                            />
                        }
                        label={t('Reports.removeBrowser')}
                    />
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={reportConfiguration.disablePersonal}
                            color="primary"
                            onChange={(e) => {
                                onChange({
                                    ...reportConfiguration,
                                    disablePersonal: e.target.checked
                                });
                                setReportConfiguration({
                                    ...reportConfiguration,
                                    disablePersonal: e.target.checked
                                });
                            }}
                        />
                    }
                    label={t('Reports.remove_personal')}
                />
                {type !== 'applicationOverlap' && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={reportConfiguration.disableInactive}
                                color="primary"
                                onChange={(e) => {
                                    onChange({
                                        ...reportConfiguration,
                                        disableInactive: e.target.checked
                                    });
                                    setReportConfiguration({
                                        ...reportConfiguration,
                                        disableInactive: e.target.checked
                                    });
                                }}
                            />
                        }
                        label={t('Reports.remove_inactive')}
                    />
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={reportConfiguration.disableUnclassified}
                            color="primary"
                            onChange={(e) => {
                                onChange({
                                    ...reportConfiguration,
                                    disableUnclassified: e.target.checked
                                });
                                setReportConfiguration({
                                    ...reportConfiguration,
                                    disableUnclassified: e.target.checked
                                });
                            }}
                        />
                    }
                    label={t('Reports.remove_unclassified')}
                />

                {type === 'accountsInventory' && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={reportConfiguration.excludeDormant}
                                color="primary"
                                onChange={(e) => {
                                    onChange({
                                        ...reportConfiguration,
                                        excludeDormant: e.target.checked
                                    });
                                    setReportConfiguration({
                                        ...reportConfiguration,
                                        excludeDormant: e.target.checked
                                    });
                                }}
                            />
                        }
                        label={t('Reports.removeDormant')}
                    />
                )}
                {type === 'accountsInventory' && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={
                                    reportConfiguration.excludePersonalAccounts
                                }
                                color="primary"
                                onChange={(e) => {
                                    onChange({
                                        ...reportConfiguration,
                                        excludePersonalAccounts:
                                            e.target.checked
                                    });
                                    setReportConfiguration({
                                        ...reportConfiguration,
                                        excludePersonalAccounts:
                                            e.target.checked
                                    });
                                }}
                            />
                        }
                        label={t('Reports.removePersonalAccounts')}
                    />
                )}
            </Flex>
        </SectionContainer>
    );
};

const getDefaultReportConfiguration = (reportType) => ({
    applicationOrCategorySelection:
        reportType === 'applicationOverlap' ? 'all' : 'application',
    applicationSelection: ['0'],
    breachSelection: ['0'],
    businessOwnerSelection: null,
    categorySelection: ['0'],
    clientSelection: ['0'],
    disableBrowsers: true,
    disableInactive: true,
    disablePersonal: reportType !== 'applicationOverlap',
    disableUnclassified: false,
    discoveryOverviewMatrixTags: ['0'],
    endTime: getEndOfMonth(),
    excludeDormant: false,
    excludePersonalAccounts: false,
    groupSelection: ['0'],
    includeInactive: false,
    includeUncataloged: true,
    includeUntagged: true,
    managementStageSelection: ['0'],
    period: 'monthly',
    platformSelection: ['0'],
    reportSelections: {},
    startTime: getStartOfMonth(),
    statusSelection: ['0'],
    tagSelection: ['0'],
    type: reportType,
    userOrGroupSelection: 'all',
    userSelection: ['0']
});

export const ReportBuilder = ({
    ignoreParams,
    onChange,
    reportConfiguration: allReportsDescriptions,
    reportSetup,
    reportType,
    runFn
}) => {
    const defaultReportConfiguration = useMemo(
        () => getDefaultReportConfiguration(reportType),
        [reportType]
    );
    const [loaded, setLoaded] = useState(false);
    const [reportConfiguration, setReportConfiguration] = useState(
        defaultReportConfiguration
    );

    useEffect(() => {
        load({
            allReportsDescriptions,
            ignoreParams,
            onChange,
            reportConfiguration,
            reportSetup,
            runFn,
            setLoaded,
            setReportConfiguration
        });
        // Disabling rule, because as things are now, all dependencies will cause an infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!loaded) {
        return null;
    }

    const { type } = reportConfiguration;

    const showRenderAppButtons =
        type !== 'application' &&
        type !== 'browser' &&
        type !== 'contracts' &&
        type !== 'microsoftOfficeOverlap' &&
        type !== 'offboard' &&
        type !== 'partnerApplicationOverview' &&
        type !== 'partnerBreachImpact' &&
        type !== 'partnerUsers' &&
        type !== 'productAdoption' &&
        type !== 'platform' &&
        type !== 'qbr';

    // Show RenderAppSelect for reports that don't have RenderAppButtons
    // but still need application selection
    const showRenderAppSelect =
        !showRenderAppButtons &&
        !(
            type === 'browser' ||
            type === 'offboard' ||
            type === 'partnerBreachImpact' ||
            type === 'partnerUsers' ||
            type === 'qbr'
        );

    return (
        <Box data-trackid="reportBuilderContainer" p="16px">
            {type === 'partnerUsers' && (
                <RenderClientOptions
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            )}
            {(type === 'browser' ||
                type === 'discovery' ||
                type === 'offboard' ||
                type === 'organizational' ||
                type === 'userApps' ||
                type === 'accountsInventory') && (
                <RenderUserOptions
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            )}
            {type !== 'browser' &&
                type !== 'businessOwner' &&
                type !== 'contracts' &&
                type !== 'discovery' &&
                type !== 'microsoftOfficeOverlap' &&
                type !== 'offboard' &&
                type !== 'partnerApplicationOverview' &&
                type !== 'partnerBreachImpact' &&
                type !== 'partnerUsers' && (
                    <RenderDateOptions
                        onChange={onChange}
                        reportConfiguration={reportConfiguration}
                        setReportConfiguration={setReportConfiguration}
                    />
                )}
            {type === 'businessOwner' && (
                <SelectOwner
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            )}

            {(reportConfiguration.type === 'businessOwner' ||
                reportConfiguration.type === 'contracts' ||
                reportConfiguration.type === 'partnerApplicationOverview') && (
                <SelectQuarter
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            )}

            {showRenderAppButtons && (
                <RenderAppButtons
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            )}

            {showRenderAppSelect && (
                <SectionContainer>
                    <RenderAppSelect
                        onChange={onChange}
                        reportConfiguration={reportConfiguration}
                        setReportConfiguration={setReportConfiguration}
                        showHeader
                    />
                </SectionContainer>
            )}

            {type === 'discovery' && (
                <MainTagSelect
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            )}
            {reportConfiguration.type === 'partnerBreachImpact' && (
                <PartnerBreachImpact
                    onChange={onChange}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            )}
            {type && (
                <RenderSubReports
                    onChange={onChange}
                    allReportsDescriptions={allReportsDescriptions}
                    reportConfiguration={reportConfiguration}
                    setReportConfiguration={setReportConfiguration}
                />
            )}
            {!(type === 'browser' || type === 'partnerUsers') && (
                <Permission required="system_admin">
                    <RenderAdvanced
                        onChange={onChange}
                        reportConfiguration={reportConfiguration}
                        setReportConfiguration={setReportConfiguration}
                    />
                </Permission>
            )}
        </Box>
    );
};
